// libraries
import { MouseEvent } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { Button, List, notification, Typography } from 'antd';
import { green } from '@ant-design/colors';

// services
import * as ReportService from '../../../services/bb-api/report';

// utils
import { txt } from '../../../locales/i18n';
import { formatDate } from '../../../utils/date';

export type ReportListContentMobileProps = {
	useLoading: [boolean, (state: boolean) => void];
	dataSource: Record<string, any>[];
	getReports: () => Promise<void>;
};

export function ReportListContentMobile(props: ReportListContentMobileProps): JSX.Element {
	const [loading, setLoading] = props.useLoading;
	const history = useHistory();

	function renderItem(item: Record<string, any>): JSX.Element {
		function navigateToUser(event: MouseEvent<HTMLDivElement>): void {
			event.stopPropagation();
			history.push('/users/' + item.userId);
		}

		async function handleApproveReport(event: MouseEvent<HTMLDivElement>): Promise<void> {
			event.stopPropagation();
			setLoading(true);

			try {
				await ReportService.approve(item.id);
				notification.success({ message: txt('notification.update successful') });
				await props.getReports();
			} catch (error) {
				notification.error({ message: txt('notification.update failed') });
			}

			setLoading(false);
		}

		function handleClick(): void {
			switch (item.contentType) {
				case txt('global.post'): {
					return history.push(`/posts/${item.contentId}`);
				}

				case txt('global.conversation'): {
					return history.push(`/users/${item.userId}/conversations/${item.contentId}`);
				}

				case txt('global.activity'): {
					return history.push(`/activities/${item.contentId}`);
				}

				case txt('global.union'): {
					return history.push(`/unions/${item.contentId}`);
				}
			}
		}

		return (
			<StyledListItem onClick={handleClick}>
				<div className="list-item-grid-container">
					<div style={{ display: 'contents' }} onClick={navigateToUser}>
						<Typography.Text type="secondary">{txt('global.username')}</Typography.Text>
						<Typography.Text ellipsis>{item.username}</Typography.Text>

						<Typography.Text type="secondary">Email</Typography.Text>
						<Typography.Text ellipsis>{item.userEmail}</Typography.Text>

						<Typography.Text type="secondary">{txt('global.municipality')}</Typography.Text>
						<Typography.Text ellipsis>{item.userMunicipality}</Typography.Text>
					</div>

					<Typography.Text type="secondary">{txt('global.message')}</Typography.Text>
					<Typography.Text ellipsis>{item.message}</Typography.Text>

					<Typography.Text type="secondary">Type</Typography.Text>
					<Typography.Text ellipsis>{item.contentType}</Typography.Text>

					<Typography.Text type="secondary">{txt('global.created')}</Typography.Text>
					<Typography.Text ellipsis>{formatDate(item.created)}</Typography.Text>
				</div>

				<div className="item-action-container">
					<Button onClick={handleApproveReport} type="primary" className="approve-button">
						{txt('global.approve')}
					</Button>
				</div>
			</StyledListItem>
		);
	}

	return (
		<List
			size="large"
			itemLayout="vertical"
			loading={loading}
			renderItem={renderItem}
			dataSource={props.dataSource}
		/>
	);
}

const StyledListItem = styled(List.Item)`
	.list-item-grid-container {
		display: grid;
		grid-template-columns: auto 1fr;
		gap: 0ch 1ch;
	}

	.item-action-container {
		padding-block: 1ch;
		display: flex;
		flex-direction: column;
	}

	.approve-button {
		background: ${green.primary};
		border-color: ${green.primary};
	}
`;
